import {inject, Injectable} from '@angular/core';
import {AccessType, AuthService, PermissionType} from '@services';

// Each chart follows the permission its own My Score page checks.
@Injectable()
export class DashboardAccess {
    private auth = inject(AuthService);

    readonly throughput = this.can(PermissionType.YearToDate);
    readonly facilityUtilization = this.can(PermissionType.InventoryFacilityUtilization);

    readonly attendance = this.can(PermissionType.MyScore) && this.can(PermissionType.MyScoreAttendance);
    readonly efficiency = this.can(PermissionType.NextView);
    readonly checklistCompliance = this.can(PermissionType.ChecklistCompliance);
    readonly checklistCondition = this.can(PermissionType.ChecklistCondition);
    readonly incidentReports = this.can(PermissionType.IncidentReports);

    readonly yardCapacity = this.can(PermissionType.YardCapacityScore, AccessType.Manage);
    readonly shuntProductivity = this.can(PermissionType.ShuntProductivityScore);
    readonly emptyTrailerAging = this.can(PermissionType.EmptyTrailerAgingScore);

    readonly appointmentAging = this.can(PermissionType.AppointmentAgingScore, AccessType.Manage);
    readonly tagsRequired = this.can(PermissionType.TagsRequiredScore, AccessType.Manage);
    readonly appointmentCompliance = this.can(PermissionType.AppointmentComplianceScore);
    readonly inboundProductivity = this.can(PermissionType.InboundProductivityScore);
    readonly damaged210 = this.can(PermissionType.InventoryStorageUnits210);

    readonly missing216 = this.can(PermissionType.InventoryStorageUnits216);
    readonly putaway260 = this.can(PermissionType.InventoryStorageUnits260);
    readonly blockedStock = this.can(PermissionType.BlockedStock);

    readonly orderPlanningMix = this.can(PermissionType.MyScoreOrderPlanningMix);
    readonly postProductionMdt = this.can(PermissionType.MyScorePostProductionMdt);
    readonly preProductionOrders = this.can(PermissionType.MyScorePreProductionOrders);

    readonly volumes = this.throughput || this.facilityUtilization;
    readonly ourTeam = this.attendance || this.efficiency || this.checklistCompliance || this.checklistCondition || this.incidentReports;
    readonly ourYard = this.yardCapacity || this.shuntProductivity || this.emptyTrailerAging;
    readonly inbound = this.appointmentAging || this.tagsRequired || this.appointmentCompliance || this.inboundProductivity || this.damaged210;
    readonly inventory = this.missing216 || this.putaway260 || this.blockedStock;
    readonly outbound = this.orderPlanningMix || this.postProductionMdt || this.preProductionOrders;

    // Without Manage these scores only cover the user's own data, so the request names the user, as on their My Score pages.
    ownUnlessManage(permission: PermissionType): string | null {
        return this.can(permission, AccessType.Manage) ? null : this.auth.userId;
    }

    attendanceUserId(): string | null {
        return this.auth.canViewMyScoreSection(null, PermissionType.MyScoreAttendance) ? null : this.auth.userId;
    }

    private can(permission: PermissionType, access: AccessType = AccessType.ViewOnly): boolean {
        return this.auth.checkPermission(permission, access);
    }
}
